import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

// ── Arwes tokens — mirrors panelTokens() in graphThemes.js ───────────────────
const cyan = "14,165,233";
const cyanLit = "56,189,248";

const arwesTokens = (dark) => ({
  border: dark ? `rgba(${cyan},0.38)` : `rgba(100,116,139,0.28)`,
  innerGlow: dark
    ? `radial-gradient(ellipse 80% 55% at 50% 0%, rgba(${cyan},0.07) 0%, transparent 100%)`
    : "none",
  divider: dark ? `rgba(${cyan},0.12)` : `rgba(100,116,139,0.15)`,
  mutedText: dark ? `rgba(148,163,184,0.65)` : `rgba(71,85,105,0.80)`,
});

// ─────────────────────────────────────────────────────────────────────────────
//  FloatingPanel
// ─────────────────────────────────────────────────────────────────────────────
const FloatingPanel = ({ children, t, dark, initialX, initialY, title }) => {
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ x: -9999, y: -9999 });
  const dragState = useRef(null);
  const tk = arwesTokens(dark);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cx = initialX ?? vw / 2;
    const cy = initialY ?? vh / 2;
    let x = cx < vw / 2 ? cx + 12 : cx - rect.width - 12;
    let y = cy < vh / 2 ? cy + 12 : cy - rect.height - 12;
    x = Math.max(8, Math.min(x, vw - rect.width - 8));
    y = Math.max(8, Math.min(y, vh - rect.height - 8));
    setPos({ x, y });
  }, []); // eslint-disable-line

  const onPointerDown = (e) => {
    if (
      e.target.tagName === "INPUT" ||
      e.target.tagName === "BUTTON" ||
      e.target.tagName === "SELECT"
    )
      return;
    e.preventDefault();
    dragState.current = {
      startX: e.clientX - pos.x,
      startY: e.clientY - pos.y,
    };
    const onMove = (ev) => {
      if (!dragState.current) return;
      setPos({
        x: ev.clientX - dragState.current.startX,
        y: ev.clientY - dragState.current.startY,
      });
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <>
      <style>{`
        @keyframes arwes-panel-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px rgba(${cyan},0.08),
              0 0 14px 2px rgba(${cyan},0.10),
              0 0 36px 4px rgba(${cyan},0.04);
          }
          50% {
            box-shadow:
              0 0 0 1px rgba(${cyan},0.16),
              0 0 20px 4px rgba(${cyan},0.17),
              0 0 50px 7px rgba(${cyan},0.07);
          }
        }
      `}</style>

      <motion.div
        ref={panelRef}
        className="fixed z-[60] w-[340px] rounded-lg overflow-hidden backdrop-blur-md"
        initial={{ opacity: 0, scale: 0.96, y: 6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 4 }}
        transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        style={{
          left: pos.x,
          top: pos.y,
          background:
            tk.innerGlow !== "none"
              ? `${tk.innerGlow}, ${t.surface.bgSolid}`
              : t.surface.bgSolid,
          border: `1px solid ${tk.border}`,
          animation: "arwes-panel-pulse 5s ease-in-out infinite",
          cursor: "default",
        }}
      >
        {/* Drag handle / header */}
        <div
          className="flex items-center gap-2 px-4 py-2.5"
          style={{ borderBottom: `1px solid ${tk.divider}`, cursor: "grab" }}
          onPointerDown={onPointerDown}
        >
          <div
            aria-hidden
            style={{
              width: 14,
              height: 2.5,
              borderRadius: 2,
              background: tk.border,
              flexShrink: 0,
            }}
          />
          <h3
            className={`${t.text.primary} select-none text-xs font-medium tracking-wide`}
          >
            {title}
          </h3>
        </div>

        <div className="px-4 pb-4 pt-3">{children}</div>
      </motion.div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  ModalInput
// ─────────────────────────────────────────────────────────────────────────────
const ModalInput = ({
  label,
  placeholder,
  value,
  onChange,
  onKeyDown,
  t,
  dark,
  autoFocus = true,
}) => {
  const tk = arwesTokens(dark ?? false);
  return (
    <div className="mb-3">
      <label
        className="mb-1 block text-xs font-medium tracking-wide"
        style={{ color: tk.mutedText }}
      >
        {label}
      </label>
      <input
        type="text"
        autoFocus={autoFocus}
        className={`w-full rounded-lg px-3 py-2 text-xs outline-none transition
          ${t.input.bg} border ${t.input.border} ${t.input.text} ${t.input.placeholder}
          ${t.input.focusBorder}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  ModalActions
// ─────────────────────────────────────────────────────────────────────────────
const ModalActions = ({ onCancel, onConfirm, confirmLabel, disabled, t }) => (
  <div className="flex justify-end gap-2 pt-1">
    <button
      className={`cursor-pointer rounded-lg border px-4 py-1.5 text-xs font-medium transition ${t.btn.secondary}`}
      onClick={onCancel}
    >
      Cancel
    </button>
    <button
      className="cursor-pointer rounded-lg bg-mainButtonBg px-4 py-1.5 text-xs font-medium
        text-mainText outline-none ring-inputFocus/60 transition
        hover:bg-secondaryButtonBg focus-visible:ring-2
        disabled:cursor-not-allowed disabled:opacity-40"
      disabled={disabled}
      onClick={onConfirm}
    >
      {confirmLabel}
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
//  ModalDropdown
// ─────────────────────────────────────────────────────────────────────────────
const ModalDropdown = ({ label, value, onChange, options, t, dark }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const dropRef = useRef(null);
  const tk = arwesTokens(dark ?? false);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open) setFilter("");
  }, [open]);

  const filtered = filter
    ? options.filter((o) => o.toLowerCase().includes(filter.toLowerCase()))
    : options;

  return (
    <div className="relative mb-4" ref={dropRef}>
      <label
        className="mb-1 block text-xs font-medium tracking-wide"
        style={{ color: tk.mutedText }}
      >
        {label}
      </label>
      <button
        type="button"
        className={`flex w-full cursor-pointer items-center justify-between rounded-lg
          px-3 py-2 text-left text-xs outline-none transition
          ${t.input.bg} border ${t.input.border} ${t.input.text}`}
        onClick={() => setOpen(!open)}
      >
        <span className="truncate">{value || "Select..."}</span>
        {React.createElement(ChevronDown, {
          size: 13,
          style: {
            opacity: 0.5,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
            flexShrink: 0,
          },
        })}
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg backdrop-blur-lg"
          style={{
            background: t.surface.popupBg,
            border: `1px solid ${tk.border}`,
            boxShadow: t.surface.popupShadow,
          }}
        >
          {options.length > 8 && (
            <div className="p-1.5">
              <input
                type="text"
                autoFocus
                className={`w-full rounded-lg px-2.5 py-1.5 text-xs outline-none transition
                  ${t.input.bg} border ${t.input.border} ${t.input.text} ${t.input.placeholder}`}
                placeholder="Search..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <div
            className="max-h-[200px] overflow-y-auto overscroll-contain"
            style={{ scrollbarWidth: "thin" }}
          >
            {filtered.length === 0 ? (
              <div className={`px-3 py-2 text-xs ${t.text.secondary}`}>
                No matches
              </div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-xs transition ${t.input.text}`}
                  style={{
                    background:
                      opt === value ? `rgba(${cyan},0.10)` : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (opt !== value)
                      e.currentTarget.style.background = `rgba(${cyan},0.06)`;
                  }}
                  onMouseLeave={(e) => {
                    if (opt !== value)
                      e.currentTarget.style.background = "transparent";
                  }}
                  onClick={() => {
                    onChange({ target: { value: opt } });
                    setOpen(false);
                  }}
                >
                  {opt === value && (
                    <span
                      style={{
                        color: `rgb(${cyan})`,
                        fontSize: 9,
                        flexShrink: 0,
                      }}
                    >
                      ●
                    </span>
                  )}
                  <span className="truncate">{opt}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  ModalTextarea
// ─────────────────────────────────────────────────────────────────────────────
const ModalTextarea = ({ label, placeholder, value, onChange, t, dark }) => {
  const tk = arwesTokens(dark ?? false);
  return (
    <div className="mb-3">
      <label
        className="mb-1 block text-xs font-medium tracking-wide"
        style={{ color: tk.mutedText }}
      >
        {label}
      </label>
      <textarea
        className={`w-full resize-none rounded-lg px-3 py-2 text-xs outline-none transition
          ${t.input.bg} border ${t.input.border} ${t.input.text} ${t.input.placeholder}
          ${t.input.focusBorder}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        rows={2}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  ModalToggle
// ─────────────────────────────────────────────────────────────────────────────
const ModalToggle = ({ label, checked, onChange, t, dark }) => {
  const tk = arwesTokens(dark ?? false);
  return (
    <div className="mb-3 flex items-center justify-between">
      <label
        className="text-xs font-medium tracking-wide"
        style={{ color: tk.mutedText }}
      >
        {label}
      </label>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border transition duration-200"
        style={{
          borderColor: checked ? `rgba(${cyan},0.60)` : tk.divider,
          background: checked ? `rgba(${cyan},0.85)` : "transparent",
          boxShadow: checked ? `0 0 8px rgba(${cyan},0.25)` : "none",
        }}
      >
        <span
          className="pointer-events-none absolute top-0.5 inline-block h-5 w-5 rounded-full shadow-sm transition-all duration-200"
          style={{
            transform: checked ? "translateX(20px)" : "translateX(2px)",
            background: checked
              ? "rgba(240,246,255,0.95)"
              : "rgba(148,163,184,0.50)",
          }}
        />
      </button>
    </div>
  );
};

export {
  FloatingPanel,
  ModalInput,
  ModalActions,
  ModalDropdown,
  ModalTextarea,
  ModalToggle,
};
